import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Elements } from '@stripe/react-stripe-js';
import { 
  User, Settings, CreditCard, Moon, Sun, Bell, Shield, 
  Calendar, Activity, TrendingUp, AlertCircle, Check, 
  Crown, Zap, Star
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import stripePromise, { SUBSCRIPTION_PLANS, mockSubscriptionData } from '../config/stripe';
import SubscriptionManager from './SubscriptionManager';

function UserProfile() {
  const { user, isLoading } = useAuth0();
  const { isDarkMode, toggleTheme } = useTheme();
  const { userData, userRole, loading: userLoading } = useUser();
  const [activeTab, setActiveTab] = useState('profile');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    marketing: true
  });

  // User profile data - synced with tai-khoan API
  // Parse company_info JSON if it exists
  const parseCompanyInfo = (userData) => {
    if (userData?.company_info) {
      try {
        const parsed = typeof userData.company_info === 'string' 
          ? JSON.parse(userData.company_info) 
          : userData.company_info;
        return parsed;
      } catch (e) {
        console.log('Could not parse company_info:', e);
        return {};
      }
    }
    return {};
  };

  const companyInfoData = parseCompanyInfo(userData);
  
  const [profileData, setProfileData] = useState({
    company: companyInfoData.company || userData?.company || '',
    phone: companyInfoData.phone || userData?.phone || '',
    address: companyInfoData.address || userData?.address || '',
    city: companyInfoData.city || userData?.city || '',
    state: companyInfoData.state || userData?.state || '',
    zip: companyInfoData.zip || userData?.zip || '',
    carrier_mc_number: companyInfoData.carrier_mc_number || userData?.carrier_mc_number || ''
  });
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Update profile data when userData changes
  useEffect(() => {
    if (userData) {
      const companyInfo = parseCompanyInfo(userData);
      setProfileData({
        company: companyInfo.company || userData.company || '',
        phone: companyInfo.phone || userData.phone || '',
        address: companyInfo.address || userData.address || '',
        city: companyInfo.city || userData.city || '',
        state: companyInfo.state || userData.state || '',
        zip: companyInfo.zip || userData.zip || '',
        carrier_mc_number: companyInfo.carrier_mc_number || userData.carrier_mc_number || ''
      });
    }
  }, [userData]);

  useEffect(() => {
    // Simulate loading subscription data
    const loadSubscription = async () => {
      try {
        // In real app, fetch from your API
        // const response = await fetch('/api/subscription', { headers: { Authorization: `Bearer ${token}` }});
        // const data = await response.json();
        
        // For now, use mock data
        setTimeout(() => {
          setSubscription(mockSubscriptionData);
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Failed to load subscription:', error);
        setLoading(false);
      }
    };

    if (user) {
      loadSubscription();
    }
  }, [user]);

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'subscription', name: 'Subscription', icon: CreditCard },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  const handleNotificationChange = (type) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setSaveStatus({ type: '', message: '' });

    try {
      // Get the base URL for tai-khoan service
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const baseUrl = isLocal 
        ? 'http://64.225.20.106:8081'
        : '/tai-khoan';

      const fullUrl = baseUrl.startsWith('http') 
        ? `${baseUrl}/update_defaults`
        : `${window.location.origin}${baseUrl}/update_defaults`;

      // Create company_info JSON object with all profile fields
      const companyInfo = {
        company: profileData.company || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        city: profileData.city || '',
        state: profileData.state || '',
        zip: profileData.zip || '',
        carrier_mc_number: profileData.carrier_mc_number || ''
      };

      // Prepare the update data for update_defaults endpoint
      const updateData = {
        user_id: user?.sub,
        default_city: profileData.city || userData?.default_city,
        default_state: profileData.state || userData?.default_state,
        company_info: JSON.stringify(companyInfo) // Store as JSON string in company_info column
      };

      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        setSaveStatus({ type: 'success', message: 'Profile updated successfully!' });
        
        // Optionally refresh user data
        if (window.refreshUserData) {
          window.refreshUserData();
        }
      } else {
        const errorData = await response.json().catch(() => null);
        setSaveStatus({ 
          type: 'error', 
          message: errorData?.message || 'Failed to update profile. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSaving(false);
      // Clear message after 5 seconds
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  const getPlanIcon = (role) => {
    switch (role) {
      case 'Admin': return <Shield className="w-5 h-5 text-red-500" />;
      case 'Dev': return <Shield className="w-5 h-5 text-purple-500" />;
      case 'Premium': return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'Standard': return <Zap className="w-5 h-5 text-blue-500" />;
      case 'Basic': return <Star className="w-5 h-5 text-gray-500" />;
      default: return <Star className="w-5 h-5" />;
    }
  };

  if (isLoading || userLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full" />
              ) : (
                <User className="w-8 h-8" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user?.name || 'User Profile'}</h1>
              <p className="text-primary-100">{user?.email}</p>
              {userRole && (
                <div className="flex items-center space-x-2 mt-2">
                  {getPlanIcon(userRole)}
                  <span className="text-sm font-medium">{userRole} User</span>
                  {userData?.plan_desc && (
                    <span className="text-xs text-primary-200">
                      • {userData.plan_desc} Plan
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Profile Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="form-input bg-gray-50 dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="form-input bg-gray-50 dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    value={profileData.company}
                    onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
                    className="form-input"
                    placeholder="Your Company Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                    className="form-input"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={profileData.address}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                    className="form-input"
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                    className="form-input"
                    placeholder="Chicago"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={profileData.state}
                    onChange={(e) => setProfileData(prev => ({ ...prev, state: e.target.value }))}
                    className="form-input"
                    placeholder="IL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Carrier/MC Number
                  </label>
                  <input
                    type="text"
                    value={profileData.carrier_mc_number}
                    onChange={(e) => setProfileData(prev => ({ ...prev, carrier_mc_number: e.target.value }))}
                    className="form-input"
                    placeholder="MC123456"
                  />
                </div>
              </div>
              
              {/* Save Status Message */}
              {saveStatus.message && (
                <div className={`mt-4 p-3 rounded-lg ${
                  saveStatus.type === 'success' 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' 
                    : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                }`}>
                  {saveStatus.message}
                </div>
              )}
              
              <div className="mt-6">
                <button 
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Subscription & Role</h2>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* User Role */}
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full ${
                      userRole === 'Admin' ? 'bg-red-100' :
                      userRole === 'Dev' ? 'bg-purple-100' :
                      userRole === 'Premium' ? 'bg-yellow-100' :
                      userRole === 'Standard' ? 'bg-blue-100' :
                      'bg-gray-100'
                    }`}>
                      {getPlanIcon(userRole)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">User Role</h3>
                      <p className="text-gray-600 dark:text-gray-400">{userRole}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        {userRole === 'Admin' ? 'Full system access with administrative privileges' :
                         userRole === 'Dev' ? 'Developer access with testing capabilities' :
                         userRole === 'Premium' ? 'Premium features and advanced analytics' :
                         userRole === 'Standard' ? 'Standard features and basic analytics' :
                         'Basic access to core features'}
                      </p>
                    </div>
                  </div>

                  {/* Subscription Plan */}
                  {userData?.plan_desc && (
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-full bg-green-100">
                        <CreditCard className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">Subscription Plan</h3>
                        <p className="text-gray-600 dark:text-gray-400">{userData.plan_desc}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          Status: {userData.plan_status === 'active' ? 'Active' : userData.plan_status}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account Details */}
                {userData && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Account Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">User ID:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{userData.id}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Account Created:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {new Date(userData.created_date).toLocaleDateString()}
                        </span>
                      </div>
                      {userData.default_city && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Default Location:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {userData.default_city}, {userData.default_state}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Preferences</h2>
              
              {/* Theme Toggle */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {isDarkMode ? <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <Sun className="w-5 h-5 text-gray-600" />}
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Theme</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Choose your preferred color scheme
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isDarkMode ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isDarkMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Notifications */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Notifications</h3>
                <div className="space-y-4">
                  {[
                    { key: 'email', label: 'Email Notifications', desc: 'Get notified about new loads and updates' },
                    { key: 'push', label: 'Push Notifications', desc: 'Receive push notifications in your browser' },
                    { key: 'marketing', label: 'Marketing Emails', desc: 'Receive updates about new features and offers' }
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">{label}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{desc}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleNotificationChange(key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          notifications[key] ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          notifications[key] ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;